<script lang="ts">
  import type { IPageBox } from './types';

  export let totalPageSize!: number;
  export let pageSize!: number;
  export let currentPage!: number;
  export let pagingEvent!: any;

  // 呼び出し元のPaging処理（Callback）を呼び出す
  $: pageNumber = currentPage;
  $: pagingEvent(pageNumber);

  const pagenations = (() => {
    const divided = Math.floor(totalPageSize / pageSize);
    const reminder = Math.floor(totalPageSize % pageSize);

    if (divided === 0) {
      return 1;
    }
    return reminder > 0 ? divided + 1 : divided;
  })();

  // 固定要素のページ数を算出
  const pagingFirst = 1;
  const pagingLast = pagenations;
  const pagingPrevious = (() => {
    const previousPage = currentPage - 1;
    return previousPage === 0 ? 1 : previousPage;
  })();
  const pagingNext = (() => {
    const nextPage = currentPage + 1;
    return nextPage > pagenations ? pagenations : nextPage;
  })();

  // 表示用の現ページStart-End数を算出
  const currentStartPageNumber = (() => {
    return totalPageSize === 0 ? 0 : currentPage === 1 ? 1 : pageSize * (currentPage - 1) + 1;
  })();
  const currentEndPageNumber = (() => {
    const endPage = pageSize * currentPage;
    return totalPageSize < endPage ? totalPageSize : endPage;
  })();

  // Pagingの各情報を生成する
  // << (最初のページ)
  const firstPageBox: IPageBox = {
    boxNumber: pagingFirst,
    focusClass: 'box-unfocus',
    isDisabled: currentPage === pagingFirst ? true : false,
    onClick: () => (pageNumber = pagingFirst)
  };
  // < (前のページ)
  const previousPageBox: IPageBox = {
    boxNumber: pagingPrevious,
    focusClass: 'box-unfocus',
    isDisabled: currentPage === pagingPrevious ? true : false,
    onClick: () => (pageNumber = pagingPrevious)
  };
  // 中間ページ
  const pagingMiddlePageBox: IPageBox = {
    boxNumber: pagingPrevious,
    focusClass: 'box-unfocus',
    isDisabled: true,
    onClick: () => ({})
  };
  // > (次のページ)
  const nextPageBox: IPageBox = {
    boxNumber: pagingNext,
    focusClass: 'box-unfocus',
    isDisabled: currentPage === pagingNext ? true : false,
    onClick: () => (pageNumber = pagingNext)
  };
  // >> (最後のページ)
  const lastPageBox: IPageBox = {
    boxNumber: pagingLast,
    focusClass: 'box-unfocus',
    isDisabled: currentPage === pagingLast ? true : false,
    onClick: () => (pageNumber = pagingLast)
  };
  // page
  const pagingArray: number[] = (() => {
    let ret = [];
    if (pagenations === 0) {
      ret.push(1);
    } else {
      for (var i = 0; i < pagenations; i++) {
        ret.push(i + 1);
      }
    }
    return ret;
  })();
  const pagingBoxes: IPageBox[] = pagingArray.map((pagingNumber) => {
    return {
      boxNumber: pagingNumber,
      focusClass: currentPage === pagingNumber ? 'box-focus' : 'box-unfocus',
      isDisabled: false,
      onClick: () => (pageNumber = pagingNumber)
    };
  });

  const isFirst = (boxLength: number) => {
    return boxLength > 0;
  };
  const isMiddle = (boxLength: number) => {
    return isFirst(boxLength) && isLast(boxLength);
  };
  const isLast = (boxLength: number) => {
    return boxLength > 1;
  };
</script>

<div class="flex flex-row space-x-4 place-items-center">
  <div class="flex-1">
    <!-- xx件中 nn - nn を表示 -->
    <p class="text-sm text-gray-700">
      {totalPageSize} 件中 <span class="font-medium">{currentStartPageNumber}</span> -
      <span class="font-medium">{currentEndPageNumber}</span> を表示
    </p>
  </div>
  <div class="flex-1">
    <ul class="flex items-center -space-x-px h-8 text-sm">
      <!-- firstPageBox -->
      <li>
        <button
          class="rounded-l-lg box box-size {firstPageBox.focusClass}"
          on:click={firstPageBox.onClick}
          disabled={firstPageBox.isDisabled}
        >
          <span class="sr-only">First</span>
          <svg
            class="h-6 w-6 text-gray-500"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" />
            <polyline points="11 7 6 12 11 17" />
            <polyline points="17 7 12 12 17 17" />
          </svg>
        </button>
      </li>

      <!-- previousPageBox -->
      <li>
        <button
          class="box box-size {previousPageBox.focusClass}"
          on:click={previousPageBox.onClick}
          disabled={previousPageBox.isDisabled}
        >
          <span class="sr-only">Previous</span>
          <svg
            class="h-6 w-6 text-gray-500"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" />
            <polyline points="15 6 9 12 15 18" />
          </svg>
        </button>
      </li>

      <!-- pagingBoxes -->
      {#if isFirst(pagingBoxes.length)}
        {@const pagingBox = pagingBoxes[0]}
        <li>
          <button
            class="box box-size {pagingBox.focusClass}"
            on:click={pagingBox.onClick}
            disabled={pagingBox.isDisabled}
          >
            {1}
          </button>
        </li>
      {/if}

      {#if isMiddle(pagingBoxes.length)}
        <li>
          <button
            class="box box-size {pagingMiddlePageBox.focusClass}"
            on:click={pagingMiddlePageBox.onClick}
            disabled={pagingMiddlePageBox.isDisabled}
          >
            <svg
              class="h-6 w-6 text-gray-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="1" /> <circle cx="19" cy="12" r="1" /> <circle cx="5" cy="12" r="1" />
            </svg>
          </button>
        </li>
      {/if}

      {#if isLast(pagingBoxes.length)}
        {@const pagingBox = pagingBoxes[pagingBoxes.length - 1]}
        <li>
          <button
            class="box box-size {pagingBox.focusClass}"
            on:click={pagingBox.onClick}
            disabled={pagingBox.isDisabled}
          >
            {pagingBox.boxNumber}
          </button>
        </li>
      {/if}
      <!-- nextPageBox -->
      <li>
        <button
          class="box box-size {nextPageBox.focusClass}"
          on:click={nextPageBox.onClick}
          disabled={nextPageBox.isDisabled}
        >
          <span class="sr-only">Next</span>
          <svg
            class="h-6 w-6 text-gray-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </li>

      <!-- lastPageBox -->
      <li>
        <button
          class="rounded-r-lg box box-size {lastPageBox.focusClass}"
          on:click={lastPageBox.onClick}
          disabled={lastPageBox.isDisabled}
        >
          <span class="sr-only">Last</span>
          <svg
            class="h-6 w-6 text-gray-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="13 17 18 12 13 7" />
            <polyline points="6 17 11 12 6 7" />
          </svg>
        </button>
      </li>
    </ul>
  </div>
</div>

<style lang="postcss">
  .box {
    @apply flex items-center justify-center border;
  }
  .box-size {
    @apply h-10 w-10 px-3;
  }
  .box-focus {
    @apply z-10 leading-tight text-green-600 border-green-300 bg-green-50 hover:enabled:bg-green-100 hover:enabled:text-green-700;
  }
  .box-unfocus {
    @apply leading-tight text-gray-500 bg-white border-gray-300 hover:enabled:bg-gray-100 hover:enabled:text-gray-700;
  }
  button:disabled {
    @apply opacity-60 cursor-default;
  }
</style>
